import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Phone, MapPin, ChevronRight, CalendarCheck } from "lucide-react";
import WordMark from "@/components/WordMark";

const LINES = [
  { text: "PREMIUM", gold: false },
  { text: "AUTOMOTIVE CARE.", gold: false },
  { text: "REDEFINED.", gold: true },
];

export default function Hero({ onBook }) {
  const [intro, setIntro] = useState(true);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.3]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const t = setTimeout(() => setIntro(false), 1700);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative flex h-[100svh] min-h-[620px] flex-col justify-end overflow-hidden bg-espresso"
    >
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img
          src="/images/hero.webp"
          alt="Premium luxury car inside the V-Elite Drives service studio, Tirupur"
          className="h-full w-full object-cover object-[68%_center]"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/35 to-espresso/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-espresso/70 via-transparent to-transparent" />

      <motion.div
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-36 sm:px-10 md:pb-40"
        style={{ opacity: fade }}
      >
        <h1
          data-testid="hero-headline"
          className="font-display text-[13vw] font-extrabold uppercase leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl"
        >
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.gold ? "gold-text" : "text-ivory"}`}
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{
                  delay: 1.9 + i * 0.14,
                  duration: 1,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          className="mt-5 max-w-md text-sm font-light tracking-wide text-ivory/70 sm:text-base"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 0.8 }}
        >
          Precision. Expertise. Premium Care.
        </motion.p>

        <motion.div
          className="mt-8 flex flex-col gap-3 sm:flex-row"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.7, duration: 0.8 }}
        >
          <button
            onClick={onBook}
            data-testid="hero-book-service-button"
            className="group flex items-center justify-center gap-3 rounded-full bg-gradient-to-br from-gold-light to-gold-deep px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] text-ink shadow-[0_8px_40px_rgba(212,175,55,0.25)] transition-transform duration-300 hover:scale-[1.03]"
          >
            <CalendarCheck size={16} /> Book a Service
            <ChevronRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            href="tel:+919688996889"
            data-testid="hero-call-button"
            className="group flex items-center justify-center gap-3 rounded-full border border-gold/40 bg-ink/30 px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] text-ivory backdrop-blur-md transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            <Phone size={16} className="text-gold" /> Call 96889 96889
          </a>
        </motion.div>

        <motion.div
          className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-ivory/60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3, duration: 0.8 }}
        >
          <MapPin size={13} className="text-gold" /> Tirupur
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {intro && (
          <motion.div
            data-testid="intro-overlay"
            className="absolute inset-0 z-30 flex items-center justify-center bg-ink"
            exit={{ y: "-100%" }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <WordMark />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
